import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ClipboardService } from 'ngx-clipboard';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { selectors as coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { ScootaversePageService } from 'app/modules/scootaverse/services/scootaverse-page.service';
import { EventsService } from 'app/modules/social-events/services/events.service';
import { ModalConfirmComponent, ModalConfirmData } from 'app/shared/modal-confirm/modal-confirm.component';
import { ModalErrorComponent } from 'app/shared/modal-error/modal-error.component';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import SocialSettings from 'entity/SocialSettings';
import StartingPoint from 'entity/StartingPoint';
import User from 'entity/User';
import SocialPermissionType from 'enums/SocialPermissionType';
import SocialController from 'rest/SocialController';

import {
  ModalDeleteSocialComponent,
  ModalDeleteSocialData,
} from '../../modal-delete-social/modal-delete-social.component';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../../modal-simple-social/modal-simple-social.component';
import { ModalSocialInviteComponent } from '../../modal-social-invite/modal-social-invite.component';
import { ModalInviteData } from '../../modal-social-invite/ModalInviteData';
import { ModalSocialSignUpComponent } from '../../modal-social-sign-up/modal-social-sign-up.component';
import { BrowserService } from '../../services/browser.service';
import DEFAULT_SOCIAL_SETTINGS from '../../social-permission/constants';
import { socialHomePageActions, socialHomePageSelectors } from '../../state/home-page';
import {
  BRANCH_IO_IOS_APP_URL,
  DEVICE_NOT_SUPPORTED_TEXT,
  DEVICE_NOT_SUPPORTED_TITLE,
  GET_IOS_APP_TEXT,
  IOS_APP_URL,
} from '../../strings';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';

@Injectable({
  providedIn: 'root',
})
export class SocialPageService extends Loggable implements OnDestroy {
  private browserService = inject(BrowserService);
  private clipBoardService = inject(ClipboardService);
  private dialog = inject(MatDialog);
  private toasterService = inject(ToastrService);
  private router = inject(Router);
  private store = inject(Store);
  private scootaversePageService = inject(ScootaversePageService);
  private eventsService = inject(EventsService);

  private user?: User;
  private isGuest = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private draftRoom?: Social;
  private savedRoomsPage = 0;
  private savedRoomsPageSize = 20;
  private hasMoreSavedRooms = true;
  private signUpPanelClass = 'new-social-page-sign-up-modal';

  private branchIo = false;

  public constructor() {
    super();
    this.setDisplayName('SocialPageService');
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isGuest, user }) => {
        this.user = user;
        this.isGuest = isGuest;
      });

    this.store
      .select(socialHomePageSelectors.getSavedRooms)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ hasMore, page, size }) => {
        this.savedRoomsPage = page;
        this.savedRoomsPageSize = size;
        this.hasMoreSavedRooms = hasMore;
      });

    // Throw error modal when room settings are unable to be saved
    this.store
      .select(socialHomePageSelectors.editRoomError)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => {
        if (error) {
          this.dialog.open(ModalErrorComponent, {
            data: {
              title: 'Unable to Save the room settings',
              text: 'We are unable to save the room settings. Please try again later.',
            },
          });
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.branch_io)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((branchIo) => {
        this.branchIo = branchIo;
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public removeDraft(): void {
    this.info('removed draft', this.draftRoom);

    if (this.draftRoom) {
      this.handleDeleteAction({ room: this.draftRoom, isDraft: true });
    }
  }

  public async onEnterRoom(
    social: Social,
    registrationId?: string,
    eventName?: string,
    eventEndDateTime?: Date,
  ): Promise<void> {
    const check = await this.browserService.checkBrowserAvailability();

    if (!check.isAvailable && check.isIOS) {
      this.showMobileOrTabletError();
      return;
    } else if (!check.isAvailable && !check.isIOS) {
      this.showBrowserError();
      return;
    }

    if (registrationId && eventEndDateTime) {
      this.eventsService.registrationId = { registrationId, endDateTime: eventEndDateTime };
    } else {
      this.eventsService.registrationId = undefined;
    }

    this.eventsService.eventName = eventName;
    await this.router.navigate(['connected-room', social.socialRoomSetId, social.socialId]);
  }

  public onDeleteRoom(room: Social): void {
    const data = {
      modalTitle: 'Delete',
      text: 'Are you sure you want to delete this Room?',
      primaryButtonText: 'Okay',
      secondaryButtonText: 'Cancel',
    };

    const dialogRef = this.dialog.open<ModalDeleteSocialComponent, ModalDeleteSocialData>(ModalDeleteSocialComponent, {
      width: '540px',
      panelClass: 'no-padding-social-page-modal',
      backdropClass: 'social-page-backdrop',
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result) {
          this.handleDeleteAction({ room });
        }
      });
  }

  /**
   * @deprecated This method is deprecated and will be removed in a future release.
   */
  // public async onEditRoom(room: Social): Promise<void> {
  //   const roomSocialSettings = await this.getRoomSettings(room.socialId as string);

  //   const data: ModalCreateSocialData = {
  //     social: room,
  //     action: 'edit',
  //     socialSettings: roomSocialSettings,
  //   };

  //   const dialogRef = this.dialog.open<ModalCreateSocialComponent, ModalCreateSocialData, ModalCreateSocialReturnData>(
  //     ModalCreateSocialComponent,
  //     {
  //       panelClass: 'no-padding-social-page-modal',
  //       backdropClass: 'social-page-backdrop',
  //       data: data,
  //     },
  //   );

  //   dialogRef
  //     .afterClosed()
  //     .pipe(takeUntil(this.destroyed$))
  //     .subscribe(async (result) => {
  //       if (!result) {
  //         await this.router.navigate(['']);
  //         return;
  //       }
  //       const { action, room: newRoom, socialSettings } = result;
  //       if (!newRoom) {
  //         this.error('onEditRoom: editing complete, but room is undefined');
  //         return;
  //       }

  //       const { socialId } = newRoom;
  //       assertDefined(socialId);

  //       const settings: SocialSettings = {
  //         settings: socialSettings?.settings,
  //         setSettings: socialSettings?.setSettings,
  //       };
  //       this.store.dispatch(socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ({ settings: settings, roomId: socialId }));

  //       this.store.dispatch(socialHomePageActions.EDIT_ROOM_REQ({ room: newRoom }));

  //       if (action === ModalCloseActionType.SETTINGS) {
  //         await this.router.navigate([`/settings/${socialId}`], {
  //           queryParams: { 'edit-mode': 'true' },
  //         });
  //       }
  //       if (action === ModalCloseActionType.START) {
  //         await this.onEnterRoom(newRoom);
  //       }
  //     });
  // }

  public executeCopy(room: Social): void {
    if (room && room.socialId) {
      this.scootaversePageService
        .copySocial(room)
        .then(() => {
          this.scootaversePageService.fetchRooms();
          this.toasterService.success('Room duplicated successfully!', 'Success');
        })
        .catch((err: string) => {
          this.error(err);
          this.toasterService.error('Error duplicating room!', 'Error');
        });
    }
  }

  public onDuplicateRoom(room?: Social): void {
    const ref = this.dialog.open<ModalConfirmComponent, ModalConfirmData>(ModalConfirmComponent, {
      panelClass: 'duplicate-modal',
      data: {
        title: 'Duplicate Room?',
        text: '',
        customConfirmationButton: 'Duplicate',
        actionButtonFillColor: '#0C58C6',
        actionButtonColor: '#FFFFFF',
        customFillButton: true,
      },
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((confirmed: boolean) => {
        if (confirmed && room) {
          this.executeCopy(room);
        }
      });
  }

  /**
   * @deprecated This method is deprecated and will be removed in a future release.
   */
  // public async onCreateRoom({
  //   artifactId,
  //   buttonClickedLocation,
  //   isCompanyAdmin,
  //   socialRoomSetId,
  //   socialTemplate,
  // }: {
  //   buttonClickedLocation?: string;
  //   socialTemplate?: SocialTemplate;
  //   isCompanyAdmin: boolean;
  //   artifactId?: string;
  //   socialRoomSetId?: string;
  // }): Promise<void> {
  //   const { isGuest, user } = this;
  //   if (!user || isGuest) {
  //     this.throwLoginForm();
  //     return;
  //   }

  //   const check = await this.browserService.checkBrowserAvailability();

  //   if (!check.isAvailable && check.isIOS) {
  //     this.showMobileOrTabletError();
  //     return;
  //   } else if (!check.isAvailable && !check.isIOS) {
  //     this.showBrowserError();
  //     return;
  //   }

  //   // Select Random Theme
  //   const themes: SocialTheme[] = await new SocialController().getSocialThemes();
  //   if (!themes) {
  //     return;
  //   }
  //   const random = Math.floor(Math.random() * themes.length);
  //   const randomTheme = themes[random];

  //   // Default Playlist = pop
  //   const defaultPlaylist = 'a7d1dc0c-d02c-11ea-b5dd-1f9fc1e94ec8';

  //   // Build new Social
  //   let newSocial: Social;
  //   // - Use Social Template as base
  //   if (socialTemplate) {
  //     assertDefined(socialTemplate.theme);
  //     assertDefined(socialTemplate.playlist);
  //     newSocial = {
  //       name: socialTemplate.templateName,
  //       description: socialTemplate.description,
  //       themeId: socialTemplate.theme.socialThemeId,
  //       theme: socialTemplate.theme,
  //       playlistId: socialTemplate.playlist.playlistId,
  //       artifactSetId: artifactId,
  //       socialPermissionType: SocialPermissionType.PUBLIC,
  //     };

  //     // - Use Default Options
  //   } else {
  //     newSocial = {
  //       name: `${formatUserName(user)}'s Room`,
  //       description: '',
  //       themeId: randomTheme.socialThemeId,
  //       theme: randomTheme,
  //       playlistId: defaultPlaylist,
  //       socialPermissionType: SocialPermissionType.PUBLIC,
  //     };
  //   }

  //   const room = await new SocialController().createSocial(newSocial, socialRoomSetId);
  //   assertDefined(room.socialId);
  //   this.saveDefaultPermissions(room.socialId);
  //   if (room) {
  //     this.draftRoom = room;
  //     this.handleAddModal({ room, isCompanyAdmin });
  //   }
  // }

  public onShareLink({ room, user }: { room: Social; user: User }): void {
    assertDefined(room.socialURI);
    const { socialURI } = room;
    const data: ModalInviteData = {
      socialURI,
      text: 'Paste the link to your calendar event or share directly with others.',
      modalTitle: 'Share the Link to this Room',
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
    };
    this.dialog.open(ModalSocialInviteComponent, {
      panelClass: 'no-padding-social-modal',
      backdropClass: 'social-page-backdrop',
      data,
    });
  }

  /**
   * Fetch the first page of rooms
   */
  public fetchRooms(): void {
    this.store.dispatch(socialHomePageActions.FETCH_SAVED_ROOMS_REQ({ page: 0, size: this.savedRoomsPageSize }));
  }

  /**
   * Fetch the next page of rooms
   */
  public fetchMoreRooms(): void {
    if (!this.hasMoreSavedRooms) {
      return;
    }

    this.store.dispatch(
      socialHomePageActions.FETCH_SAVED_ROOMS_REQ({ page: this.savedRoomsPage + 1, size: this.savedRoomsPageSize }),
    );
  }

  private showMobileOrTabletError(): void {
    const data = {
      modalTitle: DEVICE_NOT_SUPPORTED_TITLE,
      text: GET_IOS_APP_TEXT,
      primaryButtonText: 'Get IOS App',
    };

    const url = IOS_APP_URL;
    const branchIOUrl = `${BRANCH_IO_IOS_APP_URL}?ios_deeplink_path=${window.location.href}`;

    const metaTag = document.querySelector('meta[name="apple-itunes-app"]');
    if (metaTag && this.branchIo) {
      metaTag.setAttribute('content', `app-id=1568145097, app-argument=${branchIOUrl}`);
    }

    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'no-padding-social-modal',
      data,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'primary') {
          window.location.replace(this.branchIo ? branchIOUrl : url);
        }
      });
  }

  private showBrowserError(): void {
    const data = {
      modalTitle: DEVICE_NOT_SUPPORTED_TITLE,
      text: DEVICE_NOT_SUPPORTED_TEXT,
      primaryButtonText: 'OK',
      customButtonText: 'Copy Link',
    };

    const url = window.location.hostname + window.location.pathname;
    this.clipBoardService.copyFromContent(url);

    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      width: '540px',
      panelClass: 'social-modal',
      data,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'custom') {
          this.toasterService.success('Copied to clipboard', 'Success');
        }
      });
  }

  private handleDeleteAction({ isDraft = false, room }: { room: Social; isDraft?: boolean }): void {
    const action = isDraft
      ? socialHomePageActions.DELETE_DRAFT_ROOM_REQ({ room })
      : socialHomePageActions.DELETE_ROOM_REQ({ room });
    this.store.dispatch(action);
  }

  private saveDefaultPermissions(socialId: string): void {
    this.store.dispatch(
      socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ({
        roomId: socialId,
        settings: { settings: DEFAULT_SOCIAL_SETTINGS },
      }),
    );
  }

  /**
   * @deprecated This method is deprecated and will be removed in a future release.
   */
  // public handleAddModal({ isCompanyAdmin, room }: { room: Social; isCompanyAdmin: boolean }): void {
  //   assertDefined(room.socialId);
  //   const dialogRef = this.dialog.open<ModalCreateSocialComponent, ModalCreateSocialData, ModalCreateSocialReturnData>(
  //     ModalCreateSocialComponent,
  //     {
  //       panelClass: 'no-padding-social-page-modal',
  //       backdropClass: 'social-page-backdrop',
  //       data: {
  //         social: room,
  //         action: 'add',
  //       },
  //     },
  //   );

  //   dialogRef
  //     .afterClosed()
  //     .pipe(takeUntil(this.destroyed$))
  //     .subscribe(async (result) => {
  //       this.draftRoom = undefined;

  //       if (!result) {
  //         this.handleDeleteAction({ room, isDraft: true });
  //         await this.router.navigate(['']);
  //         return;
  //       }
  //       const { action, room: updatedRoom, socialSettings } = result;

  //       switch (action) {
  //         case ModalCloseActionType.START: {
  //           if (updatedRoom) {
  //             this.store.dispatch(socialHomePageActions.EDIT_ROOM_REQ({ room: updatedRoom, open: true }));
  //             if (socialSettings) {
  //               this.store.dispatch(
  //                 socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ({
  //                   settings: socialSettings,
  //                   roomId: updatedRoom.socialId as string,
  //                 }),
  //               );
  //             }
  //             return;
  //           }
  //           assertDefined(room.socialId);
  //           void this.onEnterRoom(room);
  //           break;
  //         }

  //         case ModalCloseActionType.SETTINGS: {
  //           if (!updatedRoom) {
  //             this.error('Failed to navigate to room settings, room is undefined');
  //             return;
  //           }

  //           if (socialSettings) {
  //             this.store.dispatch(
  //               socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ({
  //                 settings: socialSettings,
  //                 roomId: updatedRoom.socialId as string,
  //               }),
  //             );
  //           }
  //           this.store.dispatch(socialHomePageActions.EDIT_ROOM_REQ({ room: updatedRoom }));
  //           await this.router.navigate([`/settings/${updatedRoom.socialId}`]);
  //           break;
  //         }

  //         case ModalCloseActionType.SAVE: {
  //           if (!updatedRoom) {
  //             this.error('Failed save room, updatedRoom is undefined');
  //           } else {
  //             if (socialSettings) {
  //               this.store.dispatch(
  //                 socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ({
  //                   settings: socialSettings,
  //                   roomId: updatedRoom.socialId as string,
  //                 }),
  //               );
  //             }
  //             this.store.dispatch(socialHomePageActions.CREATE_ROOM_REQ({ room: updatedRoom }));
  //           }
  //           break;
  //         }
  //       }
  //     });
  // }

  public throwLoginForm(): void {
    const signUpDialogRef = this.dialog.open(ModalSocialSignUpComponent, {
      panelClass: this.signUpPanelClass,
    });
    // Close when event received
    const signInSubscription = signUpDialogRef.componentInstance.shouldClose.subscribe(() => {
      signUpDialogRef.close();
    });
    // Unsubscribe once closed
    signUpDialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        signInSubscription.unsubscribe();
      });
  }

  /**
   * Fetch the room settings for a given room, by SocialId
   *
   * FIXME: move this to a more general service for rooms
   */
  public async getRoomSettings(socialId: string): Promise<SocialSettings> {
    const socialSettings = await new SocialController().getSocialSettings(socialId);
    if (Object.keys(socialSettings.settings ?? {}).length !== 0) {
      return socialSettings;
    }
    return { settings: DEFAULT_SOCIAL_SETTINGS };
  }

  // get startingPoint
  public getStartingPoint(socialRoomSetId: string): Promise<StartingPoint | void> {
    return new SocialController()
      .getStartingPoint(socialRoomSetId)
      .then((startingPoint) => {
        return startingPoint;
      })
      .catch((err) => {
        this.error(err);
      });
  }

  public async createRoomAllInfo({
    buttonClickedLocation,
    socialRoomSetId,
    socialTemplate,
  }: {
    buttonClickedLocation?: string;
    socialTemplate: Social;
    socialRoomSetId?: string;
  }): Promise<void> {
    const { isGuest, user } = this;
    if (!user || isGuest) {
      this.throwLoginForm();
      return;
    }

    const check = await this.browserService.checkBrowserAvailability();

    if (!check.isAvailable && check.isIOS) {
      this.showMobileOrTabletError();
      return;
    } else if (!check.isAvailable && !check.isIOS) {
      this.showBrowserError();
      return;
    }

    // Build new Social
    const newSocial = {
      name: socialTemplate.name,
      description: socialTemplate.description,
      themeId: socialTemplate.themeId,
      theme: socialTemplate.theme,
      playlistId: socialTemplate.playlistId,
      socialPermissionType: SocialPermissionType.PUBLIC,
      startingWidth: socialTemplate.startingWidth,
      startingHeight: socialTemplate.startingHeight,
      maxParticipants: socialTemplate.maxParticipants,
      recurring: socialTemplate.recurring,
    };

    const room = await new SocialController().createSocial(newSocial, socialRoomSetId);

    if (room.socialId) {
      this.saveDefaultPermissions(room.socialId);

      if (room && socialRoomSetId) {
        this.draftRoom = room;
        this.scootaversePageService.onEnterScootaverse(socialRoomSetId).catch((e) => {
          this.error('Error redirecting to room - ', e);
        });
      }
    }
  }
}
